import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { GridCellParams, GridColDef, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { getFamily } from 'src/shared/services/familyService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { Delete, Star, StarOutline } from '@mui/icons-material';
import { FamilyMemberNew } from '../models/familyMember';
import { MemberFamilyDeleteFamilyMemberModal } from './memberFamilyDeleteFamilyMemberModal';
import { MemberFamilyChangeMainMemberModal } from './memberFamilyChangeMainMemberModal';

type MemberFamilyDataGridProps = {
  memberId: number;
};

export const MemberFamilyDataGrid = (props: MemberFamilyDataGridProps) => {
  const { memberId } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'member_id', sort: 'asc' }]);
  const [selectedMember, setSelectedMember] = useState<FamilyMemberNew>();
  const [openDeleteFamilyMemberModal, setOpenDeleteFamilyMemberModal] = useState(false);
  const [openChangeMemberTypeModal, setOpenChangeMemberTypeModal] = useState<boolean>(false);

  const [rows, setRows] = useState<FamilyMemberNew[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: false,
    totalRows: 0,
    rowsPerPageOptions: [10, 25, 50, 100],
    pageSize: 10,
    page: 0,
  });

  const columns: GridColDef[] = [
    { field: 'memberNr', flex: 2, sortable: false, headerName: t('member:tableHeaders.member_id') },
    {
      field: 'firstName',
      flex: 4,
      sortable: false,
      headerName: t('member:tableHeaders.name'),
      renderCell: (params: GridCellParams) => {
        const { firstName, lastName } = params.row;
        return <Typography>{`${firstName} ${lastName}`}</Typography>;
      },
    },
    {
      field: 'mainMember',
      flex: 4,
      sortable: false,
      headerName: t('member:tableHeaders.status'),
      renderCell: (params: GridCellParams) => {
        const { mainMember } = params.row;
        return (
          <Typography>{mainMember ? t('member:memberAdvancedSearch.isMainMember') : t('member:memberAdvancedSearch.isFamilyMember')}</Typography>
        );
      },
    },
    {
      field: 'member_id',
      flex: 2,
      headerName: '',
      align: 'right',
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const { mainMember } = params.row;
        return (
          <Box display='flex' flexDirection='row' justifyContent='flex-end'>
            {mainMember && (
              <Tooltip describeChild title={t('member:memberDetails.headMember')} arrow placement='top-end'>
                <span>
                  <IconButton disabled sx={{ color: (thm) => `${thm.palette.text.primary} !important` }}>
                    <Star />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!mainMember && (
              <Tooltip describeChild title={`${t('member:family.changeFamilyMemberTypeTooltip')}`} arrow placement='top-end'>
                <IconButton
                  onClick={() => {
                    setSelectedMember(params.row as unknown as FamilyMemberNew);
                    setOpenChangeMemberTypeModal(true);
                  }}
                >
                  <StarOutline />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip
              describeChild
              title={mainMember ? `${t('member:family.deletMainMemberFromFmily')}` : `${t('member:family.deletMemberFromFmily')}`}
              placement='top-end'
              arrow
            >
              <span>
                <IconButton
                  disabled={mainMember}
                  onClick={() => {
                    setSelectedMember(params.row as unknown as FamilyMemberNew);
                    setOpenDeleteFamilyMemberModal(true);
                  }}
                >
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  // const onPageChange = (newPageIndex: number) => {
  //   updateData('page', newPageIndex);
  // };

  // const onPageSizeChange = (newPageSize: number) => {
  //   updateData('page', 0);
  //   updateData('pageSize', newPageSize);
  // };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    try {
      const res = await getFamily(data.page, data.pageSize, sortModel[0].field, sortModel[0].sort?.toString(), memberId);
      if (isMounted()) {
        updateData('totalRows', res.data.totalItems);
        const memberResult = extendObjectWithId(res.data.results, 'memberNr');
        setRows(memberResult);
      }
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log(error);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
  }, [data.page, data.pageSize, isMounted, memberId, sortModel]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Box>
      <DataGrid<FamilyMemberNew>
        autoHeight
        columns={columns}
        rows={rows}
        dataModel={data}
        disableSelectionOnClick
        checkboxSelection={false}
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
        selectionModel={selectionModel}
        onPageChange={() => ConsoleHelper.log('herj')}
        onPageSizeChange={() => ConsoleHelper.log('herj')}
      />
      {selectedMember && openDeleteFamilyMemberModal && (
        <MemberFamilyDeleteFamilyMemberModal
          member={selectedMember}
          modalOpen={openDeleteFamilyMemberModal}
          onClose={() => setOpenDeleteFamilyMemberModal(false)}
          refetchOnDelete={() => {
            fetchData();
            setOpenDeleteFamilyMemberModal(false);
          }}
        />
      )}
      {selectedMember && openChangeMemberTypeModal && (
        <MemberFamilyChangeMainMemberModal
          onClose={() => setOpenChangeMemberTypeModal(false)}
          member={selectedMember}
          modalOpen={openChangeMemberTypeModal}
          successCallback={() => {
            fetchData();
            setOpenChangeMemberTypeModal(false);
          }}
        />
      )}
    </Box>
  );
};
